import React, { useEffect, useId, useLayoutEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

type Bounds = {
  left: number;
  top: number;
  right: number;
  bottom: number;
};

type Viewport = {
  w: number;
  h: number;
};

interface AstroSpotlightTourProps {
  targetRefs: React.RefObject<HTMLElement>[];
  onClose: () => void;
  duration?: number;
}

const getViewport = (): Viewport => ({
  w: typeof window !== 'undefined' ? window.innerWidth : 1024,
  h: typeof window !== 'undefined' ? window.innerHeight : 768,
});

const useViewport = () => {
  const [viewport, setViewport] = useState<Viewport>(() => getViewport());

  useEffect(() => {
    const updateViewport = () => setViewport(getViewport());
    window.addEventListener('resize', updateViewport);
    window.addEventListener('orientationchange', updateViewport);
    return () => {
      window.removeEventListener('resize', updateViewport);
      window.removeEventListener('orientationchange', updateViewport);
    };
  }, []);

  return viewport;
};

export function AstroSpotlightTour({ targetRefs, onClose, duration = 5000 }: AstroSpotlightTourProps) {
  const { t } = useTranslation('chat');
  const viewport = useViewport();
  const [bounds, setBounds] = useState<Bounds | null>(null);
  const [msLeft, setMsLeft] = useState(duration);
  const [visible, setVisible] = useState(false);
  const svgBaseId = useId().replace(/:/g, '');
  const maskId = `astro-spotlight-mask-${svgBaseId}`;
  const vignetteId = `astro-spotlight-vignette-${svgBaseId}`;

  useLayoutEffect(() => {
    const measure = () => {
      const rects = targetRefs
        .map((ref) => ref.current?.getBoundingClientRect())
        .filter((rect): rect is DOMRect => Boolean(rect));

      if (rects.length === 0) {
        setBounds(null);
        return;
      }

      setBounds({
        left: Math.min(...rects.map((rect) => rect.left)),
        top: Math.min(...rects.map((rect) => rect.top)),
        right: Math.max(...rects.map((rect) => rect.right)),
        bottom: Math.max(...rects.map((rect) => rect.bottom)),
      });
    };

    measure();
    const timeouts = [60, 180, 360, 720].map((delay) => window.setTimeout(measure, delay));
    const interval = window.setInterval(measure, 250);
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    return () => {
      timeouts.forEach((timeout) => window.clearTimeout(timeout));
      window.clearInterval(interval);
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [targetRefs, viewport.w, viewport.h]);

  useEffect(() => {
    const raf = window.requestAnimationFrame(() => setVisible(true));
    const tick = 50;
    const interval = window.setInterval(() => {
      setMsLeft((prev) => {
        const next = prev - tick;
        if (next <= 0) {
          window.clearInterval(interval);
          onClose();
          return 0;
        }
        return next;
      });
    }, tick);

    return () => {
      window.cancelAnimationFrame(raf);
      window.clearInterval(interval);
    };
  }, [onClose]);

  if (!bounds) return null;

  const padding = viewport.w < 480 ? 10 : 14;
  const x = bounds.left - padding;
  const y = bounds.top - padding;
  const width = bounds.right - bounds.left + padding * 2;
  const height = bounds.bottom - bounds.top + padding * 2;
  const rx = height / 2;
  const cx = x + width / 2;
  const bottomY = y + height;

  const secondsLeft = Math.ceil(msLeft / 1000);
  const progress = 1 - msLeft / duration;
  const ringR = 12;
  const ringC = 2 * Math.PI * ringR;

  const margin = 16;
  const captionWidth = Math.min(340, viewport.w - margin * 2);
  const captionLeftPreferred = cx - captionWidth / 2;
  const captionLeft = Math.max(margin, Math.min(captionLeftPreferred, viewport.w - captionWidth - margin));
  const arrowLeft = Math.max(16, Math.min(cx - captionLeft, captionWidth - 16));

  const captionGap = 20;
  const captionEdgeGap = viewport.w < 480 ? 12 : 16;
  const captionEstHeight = viewport.w < 480 ? 240 : 220;
  const spaceBelow = viewport.h - bottomY - captionGap - captionEdgeGap;
  const spaceAbove = y - captionGap - captionEdgeGap;
  const captionBelow = spaceBelow >= captionEstHeight || (spaceBelow >= spaceAbove && spaceAbove < captionEstHeight);
  const captionMaxHeight = Math.max(0, captionBelow ? spaceBelow : spaceAbove);
  const closeAtTop = bottomY > viewport.h - 100;

  return (
    <div
      className="fixed inset-0 z-[1000]"
      style={{
        opacity: visible ? 1 : 0,
        transition: 'opacity 500ms cubic-bezier(0.4, 0, 0.2, 1)',
      }}
      role="dialog"
      aria-modal="true"
      aria-label={t('chat.astro_tour.title', 'For birth charts and compatibility')}
    >
      <style>{`
        @keyframes st-fadeIn {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        @keyframes st-slideUp {
          from { opacity: 0; transform: translateY(10px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      <svg className="absolute inset-0 h-full w-full" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <mask id={maskId}>
            <rect width="100%" height="100%" fill="white" />
            <rect x={x} y={y} width={width} height={height} rx={rx} ry={rx} fill="black" />
          </mask>
          <radialGradient id={vignetteId} cx="50%" cy="50%" r="70%">
            <stop offset="40%" stopColor="rgba(10,8,18,0)" />
            <stop offset="100%" stopColor="rgba(10,8,18,0.35)" />
          </radialGradient>
        </defs>

        <rect width="100%" height="100%" fill="#0a0812" opacity="0.78" mask={`url(#${maskId})`} />
        <rect width="100%" height="100%" fill={`url(#${vignetteId})`} mask={`url(#${maskId})`} />

        <rect
          x={x - 40}
          y={y - 40}
          width={width + 80}
          height={height + 80}
          rx={rx + 40}
          ry={rx + 40}
          fill="none"
          stroke="rgba(255, 210, 150, 0.12)"
          strokeWidth="40"
          style={{ filter: 'blur(14px)' }}
        />

        <rect
          x={x}
          y={y}
          width={width}
          height={height}
          rx={rx}
          ry={rx}
          fill="none"
          stroke="rgba(255,255,255,0.95)"
          strokeWidth="1.5"
          style={{ filter: 'drop-shadow(0 0 6px rgba(255, 220, 170, 0.4))' }}
        />

        <rect
          x={x - 4}
          y={y - 4}
          width={width + 8}
          height={height + 8}
          rx={rx + 4}
          ry={rx + 4}
          fill="none"
          stroke="rgba(255,255,255,0.35)"
        >
          <animate attributeName="stroke-width" values="1;4;1" dur="2.6s" repeatCount="indefinite" />
          <animate attributeName="opacity" values="0.5;0.05;0.5" dur="2.6s" repeatCount="indefinite" />
        </rect>
      </svg>

      <div
        className="absolute"
        style={{
          left: captionLeft,
          width: captionWidth,
          top: captionBelow ? bottomY + captionGap : undefined,
          bottom: captionBelow ? undefined : viewport.h - (y - captionGap),
          animation: 'st-slideUp 700ms cubic-bezier(0.16, 1, 0.3, 1) both',
          animationDelay: '220ms',
        }}
      >
        <div className="relative">
          <div
            className="absolute"
            style={{
              [captionBelow ? 'top' : 'bottom']: -5,
              left: arrowLeft,
              width: 12,
              height: 12,
              background: '#faf6ef',
              transform: 'translateX(-50%) rotate(45deg)',
            }}
          />
          <div
            className="relative rounded-[14px]"
            style={{
              background: '#faf6ef',
              padding: viewport.w < 480 ? '18px 20px' : '20px 24px',
              boxSizing: 'border-box',
              maxHeight: captionMaxHeight,
              overflowY: 'auto',
              boxShadow:
                '0 22px 60px -10px rgba(0,0,0,0.45), 0 2px 8px rgba(0,0,0,0.15), 0 0 0 1px rgba(255,255,255,0.04)',
            }}
          >
            <div
              className="mb-2 text-[10px] font-medium uppercase"
              style={{ letterSpacing: '0.22em', color: '#b07420' }}
            >
              {t('chat.astro_tour.eyebrow', 'Personal Jyotisha')}
            </div>
            <div
              className="mb-1.5 leading-[1.15] tracking-tight"
              style={{
                fontFamily: 'Georgia, serif',
                color: '#1a1410',
                fontSize: viewport.w < 480 ? 20 : 22,
              }}
            >
              {t('chat.astro_tour.title', 'For birth charts and compatibility')}
            </div>
            <div
              className="leading-[1.55]"
              style={{
                color: '#6b6153',
                fontSize: viewport.w < 480 ? 13 : 13.5,
              }}
            >
              {t(
                'chat.astro_tour.body',
                'Use Astro Mode for your personal Vedic birth chart, or Astro Compatibility for relationship matching. Your research chat stays available here too.',
              )}
            </div>
          </div>
        </div>
      </div>

      <button
        type="button"
        onClick={onClose}
        className="absolute left-1/2 flex min-h-[44px] items-center gap-3 rounded-full"
        style={{
          [closeAtTop ? 'top' : 'bottom']: viewport.w < 480 ? 24 : 40,
          transform: 'translateX(-50%)',
          padding: '10px 18px',
          animation: 'st-fadeIn 600ms ease 400ms both',
          transition: 'background 200ms ease',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = 'rgba(255,255,255,0.06)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'transparent';
        }}
      >
        <span className="relative flex h-7 w-7 shrink-0 items-center justify-center">
          <svg viewBox="0 0 28 28" className="absolute inset-0 h-full w-full" style={{ transform: 'rotate(-90deg)' }}>
            <circle cx="14" cy="14" r={ringR} fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth="1.2" />
            <circle
              cx="14"
              cy="14"
              r={ringR}
              fill="none"
              stroke="rgba(255, 220, 170, 0.92)"
              strokeWidth="1.2"
              strokeDasharray={ringC}
              strokeDashoffset={ringC * progress}
              strokeLinecap="round"
              style={{ transition: 'stroke-dashoffset 100ms linear' }}
            />
          </svg>
          <span className="text-[10px] font-medium tabular-nums" style={{ color: 'rgba(255,255,255,0.88)' }}>
            {secondsLeft}
          </span>
        </span>
        <span
          className="whitespace-nowrap text-[11px] font-medium uppercase"
          style={{ letterSpacing: '0.28em', color: 'rgba(255,255,255,0.78)' }}
        >
          {t('chat.astro_tour.close', 'Close')}
        </span>
      </button>
    </div>
  );
}

export default AstroSpotlightTour;
